import { describeValidationError } from "../lib/apiErrors";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance, loginRequest, forgetEntraHint, ENTRA_HINT_KEY } from "../auth/msalConfig";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Password accounts (any role), Google SSO and SAML sessions sign in against our own
// API rather than Entra ID.
//
// Nothing secret is kept in browser storage. A session's refresh token lives in an
// HttpOnly, Secure cookie on the API (backend/src/lib/sessionCookies.ts) that no script
// on this page can read, and the short-lived access token lives in this module's memory,
// per tab. A new tab or a reload fetches a fresh access token with the cookie.
//
// What storage does hold are markers, none of them usable on their own:
//   - BASE_MARKER (localStorage): this browser has a base session — the person's own
//     sign-in, shared by every tab. Removing it is how a sign-out in one tab reaches the
//     others. What keeps a shared machine safe is the ten-minute inactivity sign-out
//     (components/InactivityWatcher.tsx) and the seven-day life of the refresh token.
//   - SWITCH_SLOT (sessionStorage): this tab is inside a role switch (consultant → auditor
//     portal or own-organisation client portal, platform admin → demo persona). A switch
//     is its own session in its own cookie slot, named by this random id, so it belongs to
//     the tab that made it and a consultant can keep their workspace in one tab and the
//     auditor portal in another. Leaving the role removes it and the session underneath
//     reappears.
//   - PARENT_SLOT (sessionStorage): a switch made before a second one, restored on leaving.
//
// Every request names the slot it acts on in the X-Abacas-Session header. A custom header
// is always preflighted, so no other site can make the browser refresh or end a session.
const BASE_MARKER = "abacas.localSession";
const SWITCH_SLOT = "abacas.switchSlot";
const PARENT_SLOT = "abacas.parentSwitchSlot";
const BASE_SLOT = "base";
const SESSION_HEADER = "X-Abacas-Session";

// Keys from before refresh tokens moved into cookies. Read once to carry a signed-in
// browser across (migrateLegacySession), then removed for good.
const LEGACY_ACCESS_KEY = "abacas.localAccessToken";
const LEGACY_REFRESH_KEY = "abacas.localRefreshToken";
const LEGACY_PARENT_KEY = "abacas.parentLocalSession";

/** This tab's access token for the slot it is acting on. Memory only. */
let memAccess: { slot: string; token: string } | null = null;
/**
 * A restricted session (a password account enrolling its authenticator app): an access
 * token with no refresh token behind it. It lives in this tab's memory alone and is gone on
 * reload, which simply means signing in again to finish enrolling.
 */
let restricted = false;

function safeGet(store: Storage, key: string): string | null {
  try { return store.getItem(key); } catch { return null; }
}
function safeSet(store: Storage, key: string, value: string): void {
  try { store.setItem(key, value); } catch { /* storage unavailable: the session lasts as long as the tab */ }
}
function safeRemove(store: Storage, key: string): void {
  try { store.removeItem(key); } catch { /* ignore */ }
}

/** The slot this tab is acting on: its role switch, else the base session. */
function activeSlot(): string {
  return safeGet(sessionStorage, SWITCH_SLOT) ?? BASE_SLOT;
}

function newSlotId(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function currentAccessToken(): string | null {
  return memAccess && memAccess.slot === activeSlot() ? memAccess.token : null;
}

// When a consultant is viewing a client's portal, all API calls automatically
// include ?organizationId so the server scopes data to that client.
let _viewingOrgId: string | null = null;
export function setViewingOrgId(id: string | null) { _viewingOrgId = id; }
/** The organisation API calls are currently scoped to (null = the user's own); caches key on it. */
export function getViewingOrgId(): string | null { return _viewingOrgId; }

/**
 * Records the session a sign-in step on the slot this tab is acting on just produced.
 * `refreshable: false` is the restricted enrolment session, which cannot be renewed.
 */
export function setLocalSession(accessToken: string, refreshable: boolean) {
  const slot = activeSlot();
  memAccess = { slot, token: accessToken };
  restricted = !refreshable;
  if (refreshable && slot === BASE_SLOT) safeSet(localStorage, BASE_MARKER, "1");
}

/**
 * Installs the session a sign-in just produced. Always the base session, and always on
 * its own: a role switch left in this tab belongs to whoever was signed in before, so it
 * is ended rather than left sitting on top of the new sign-in. `accessToken` is null when
 * the sign-in left only the cookie (Google SSO and SAML redirects); the first request then
 * fetches one.
 */
export function startLocalSession(accessToken: string | null, refreshable: boolean) {
  for (const key of [SWITCH_SLOT, PARENT_SLOT]) {
    const slot = safeGet(sessionStorage, key);
    if (slot) endSlot(slot);
    safeRemove(sessionStorage, key);
  }
  removeLegacyKeys();
  memAccess = accessToken ? { slot: BASE_SLOT, token: accessToken } : null;
  restricted = !refreshable;
  if (refreshable) safeSet(localStorage, BASE_MARKER, "1");
  else safeRemove(localStorage, BASE_MARKER);
}

/**
 * Calls back when another tab ends the shared session — a sign-out, or the inactivity
 * watcher firing wherever the user last was — so this tab follows it instead of staying
 * open until its own access token expires. A tab inside its own role switch keeps that
 * switch: it is a separate session, and the tab that signed out could not end it.
 */
export function onLocalSessionEnded(handler: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.storageArea !== localStorage) return;
    // An Entra session signed out in another tab: the hint goes, and this tab's own
    // (per-tab) token cache must follow it.
    if (e.key === ENTRA_HINT_KEY && e.newValue === null && msalInstance.getAllAccounts().length > 0) {
      handler();
      return;
    }
    if (e.key !== null && e.key !== BASE_MARKER) return;   // null = the whole store was cleared
    if (safeGet(localStorage, BASE_MARKER)) return;        // still signed in
    if (safeGet(sessionStorage, SWITCH_SLOT)) return;      // this tab is inside a role switch
    if (memAccess?.slot === BASE_SLOT) memAccess = null;
    handler();
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

/** Ends one slot's session on the server: revokes its refresh token and clears its cookie. */
function endSlot(slot: string) {
  void fetch(`${API_BASE_URL}/auth/logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json", [SESSION_HEADER]: slot },
    body: "{}",
    credentials: "include",
    keepalive: true,
  }).catch(() => { /* best effort — the cookie expires with the token */ });
}

/**
 * Starts a role switch (POST /auth/select-role, /auth/view-as-persona) in this tab only,
 * leaving the base session in place for the tabs that did not switch. The switch gets a
 * fresh cookie slot; a switch made while already inside one is kept so leaving returns
 * through it.
 */
export async function startRoleSwitch<T extends { accessToken: string }>(path: string, body: unknown): Promise<T> {
  const slot = newSlotId();
  const data = await apiRequest<T>(path, { method: "POST", body, sessionSlot: slot });
  const current = safeGet(sessionStorage, SWITCH_SLOT);
  if (current) safeSet(sessionStorage, PARENT_SLOT, current);
  safeSet(sessionStorage, SWITCH_SLOT, slot);
  memAccess = { slot, token: data.accessToken };
  restricted = false;
  return data;
}

/**
 * Leaves the session this tab is acting on: a role switch (restoring the one underneath,
 * if any) or, outside a switch, the base session itself.
 *
 * `revoke: false` is for a session the API has already refused: there is nothing left to
 * revoke, only the marker to remove.
 */
export function popLocalSession(revoke = true) {
  const slot = safeGet(sessionStorage, SWITCH_SLOT);
  memAccess = null;
  restricted = false;
  if (!slot) {
    if (revoke) endSlot(BASE_SLOT);
    safeRemove(localStorage, BASE_MARKER);
    return;
  }
  if (revoke) endSlot(slot);
  safeRemove(sessionStorage, SWITCH_SLOT);
  const parent = safeGet(sessionStorage, PARENT_SLOT);
  safeRemove(sessionStorage, PARENT_SLOT);
  if (parent) safeSet(sessionStorage, SWITCH_SLOT, parent);
}

function removeLegacyKeys() {
  safeRemove(localStorage, LEGACY_ACCESS_KEY);
  safeRemove(localStorage, LEGACY_REFRESH_KEY);
  safeRemove(sessionStorage, LEGACY_ACCESS_KEY);
  safeRemove(sessionStorage, LEGACY_REFRESH_KEY);
  safeRemove(sessionStorage, LEGACY_PARENT_KEY);
}

/** Signs out of every local session: this tab's role switches and the session it is signed in with. */
export function clearLocalSession() {
  for (const key of [SWITCH_SLOT, PARENT_SLOT]) {
    const slot = safeGet(sessionStorage, key);
    if (slot) endSlot(slot);
    safeRemove(sessionStorage, key);
  }
  endSlot(BASE_SLOT);
  // A token stored before the move to cookies dies with the sign-out too.
  const legacy = safeGet(localStorage, LEGACY_REFRESH_KEY);
  if (legacy) {
    void fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: legacy }), keepalive: true,
    }).catch(() => { /* best effort */ });
  }
  removeLegacyKeys();
  safeRemove(localStorage, BASE_MARKER);
  safeRemove(sessionStorage, "abacas.sessionPortalRole");
  forgetEntraHint();
  memAccess = null;
  restricted = false;
  // The consultant's recent-client ids (nav/consultantNav.ts) are per user
  // already; a sign-out - including the inactivity one - also removes them so
  // a shared machine keeps no trace. Other nav.* keys are browser preferences
  // (collapsed rail, open groups) and deliberately survive.
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("nav.recentClients.")) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch { /* storage may be unavailable */ }
}

/**
 * Whether this tab holds a local (password / Google SSO / SAML / role-switch) session.
 * Exported so components ask this rather than reading a storage key themselves —
 * where the session is kept is this module's business alone.
 */
export function hasLocalSession() {
  return !!(
    safeGet(sessionStorage, SWITCH_SLOT)
    || safeGet(localStorage, BASE_MARKER)
    || safeGet(localStorage, LEGACY_REFRESH_KEY)
    || (restricted && memAccess)
  );
}

/**
 * A browser signed in before refresh tokens moved into cookies holds its token in
 * localStorage. Hand it to the API once — it rotates it into the base cookie — and delete
 * it. A role switch stored the same way is simply ended: it was a per-tab session.
 */
let legacyMigration: Promise<void> | null = null;
function migrateLegacySession(): Promise<void> {
  if (legacyMigration) return legacyMigration;
  legacyMigration = (async () => {
    const legacySwitch = safeGet(sessionStorage, LEGACY_REFRESH_KEY);
    if (legacySwitch) {
      void fetch(`${API_BASE_URL}/auth/logout`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: legacySwitch }), keepalive: true,
      }).catch(() => { /* best effort */ });
    }
    const legacy = safeGet(localStorage, LEGACY_REFRESH_KEY);
    removeLegacyKeys();
    if (!legacy) return;
    try {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", [SESSION_HEADER]: BASE_SLOT },
        body: JSON.stringify({ refreshToken: legacy }),
        credentials: "include",
      });
      if (!res.ok) return;
      const data = await res.json() as { accessToken: string };
      memAccess = { slot: BASE_SLOT, token: data.accessToken };
      safeSet(localStorage, BASE_MARKER, "1");
    } catch { /* offline: the person signs in again */ }
  })();
  return legacyMigration;
}

async function getAuthHeader(): Promise<string | null> {
  if (safeGet(localStorage, LEGACY_REFRESH_KEY) || safeGet(sessionStorage, LEGACY_REFRESH_KEY)) await migrateLegacySession();
  if (hasLocalSession()) {
    let token = currentAccessToken();
    if (!token && !restricted) {
      await refreshLocalSession();
      token = currentAccessToken();
    }
    return token ? `Bearer ${token}` : null;
  }
  const account = msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return `Bearer ${result.accessToken}`;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      // Cached tokens can't satisfy the scope — re-authenticate to get fresh
      // consent or tokens (standard MSAL pattern for interaction-required errors).
      await msalInstance.loginRedirect(loginRequest);
      return null; // loginRedirect navigates away; never reached
    }
    // Other errors (network issues, BrowserAuthError): return null so the
    // caller receives a 401 rather than hanging on a popup.
    return null;
  }
}

// Refresh tokens are single-use: POST /auth/refresh rotates the one in the cookie and
// revokes it. A page load fires many requests at once, so one in-flight refresh is shared
// by every caller that arrives while it runs.
let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * `refreshed` — a new access token is in memory and the cookie has rotated. `rejected` —
 * this session cannot be renewed: the API refused it, or it is the restricted enrolment
 * session. `unavailable` — nothing was learned, because the request never reached a
 * healthy API; the session is not known to be bad and must not be torn down over it.
 */
type RefreshOutcome = "refreshed" | "rejected" | "unavailable";

// The base cookie is shared by every tab, so two tabs waking at once would present the
// same refresh token and the second would be refused. Web Locks serialises them across
// the origin; the second simply presents the token the first rotated in. A browser
// without it falls back to the per-tab guard alone.
function withRefreshLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = navigator.locks;
  if (!locks?.request) return run();
  return locks.request("abacas.refresh", run) as Promise<T>;
}

async function refreshLocalSession(): Promise<RefreshOutcome> {
  if (refreshInFlight) return refreshInFlight;
  if (restricted) return "rejected";
  const slot = activeSlot();
  const run = withRefreshLock(async (): Promise<RefreshOutcome> => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", [SESSION_HEADER]: slot },
        body: "{}",
        credentials: "include",
      });
      if (res.status === 401 || res.status === 400) return "rejected";
      if (!res.ok) return "unavailable";   // 5xx / gateway — the API is unwell, not the session
      const data = await res.json() as { accessToken: string };
      memAccess = { slot, token: data.accessToken };
      return "refreshed";
    } catch {
      return "unavailable";               // offline, CORS, cold start
    }
  });
  refreshInFlight = run;
  void run.finally(() => { refreshInFlight = null; });
  return run;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | undefined>;
  signal?: AbortSignal;
  /** The session slot to name instead of this tab's own (a role switch being started). */
  sessionSlot?: string;
}

/**
 * Human-readable text for an API failure. Guards such as requirePermission
 * answer `{ error: "<machine code>", message: "<sentence>" }`, most routes
 * answer `{ error: "<sentence>" }`, and zod failures put an object in `error`
 * — so `message` wins, then a string `error`, then the caller's fallback.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const payload = err.payload as { error?: unknown; message?: unknown } | undefined;
    if (typeof payload?.message === "string" && payload.message) return payload.message;
    if (typeof payload?.error === "string" && payload.error) return payload.error;
    const validation = describeValidationError(payload?.error);
    if (validation) return validation;
  }
  return fallback;
}

/**
 * Human-readable reason for a failed response. Routes reply either
 * { error: code, message: sentence } or the older { error: sentence }; a bare
 * code such as "insufficient_permission" is left to `code` so a catch block
 * showing `message` never prints an identifier at the user.
 */
function describeApiError(status: number, payload: unknown): string {
  if (payload && typeof payload === "object") {
    const { message, error } = payload as { message?: unknown; error?: unknown };
    if (typeof message === "string" && message) return message;
    if (typeof error === "string" && /\s/.test(error)) return error;
    const validation = describeValidationError(error);
    if (validation) return validation;
  }
  return `API error ${status}`;
}

export class ApiError extends Error {
  status: number;
  payload: unknown;
  code: string | null;
  constructor(status: number, payload: unknown) {
    super(describeApiError(status, payload));
    this.status = status;
    this.payload = payload;
    this.code = typeof payload === "object" && payload && "error" in payload ? String((payload as { error: unknown }).error) : null;
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
  const authHeader = await getAuthHeader();
  const url = new URL(path, API_BASE_URL);
  if (_viewingOrgId) url.searchParams.set("organizationId", _viewingOrgId);
  if (options.query) {
    for (const [k, v] of Object.entries(options.query)) if (v !== undefined) url.searchParams.set(k, v);
  }

  const res = await fetch(url.toString(), {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      // The session slot this request acts on (see the top of this file). Sign-in, refresh,
      // role-switch and sign-out routes read their HttpOnly cookie from it.
      [SESSION_HEADER]: options.sessionSlot ?? activeSlot(),
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
    // Sends the session cookies (only ever read on /auth) and the short-lived OAuth
    // nonce that ties a Google/M365 connect flow to this browser.
    credentials: "include",
  });

  if (res.status === 401 && hasLocalSession() && !isRetry && !options.sessionSlot) {
    // The access token this request actually carried. A session that has moved on since
    // we sent the request was refreshed meanwhile, and the 401 is stale news rather than
    // a reason to sign the user out.
    const sentWith = authHeader ? authHeader.slice("Bearer ".length) : null;
    if (currentAccessToken() !== sentWith) return apiRequest<T>(path, options, true);

    const outcome = await refreshLocalSession();
    if (outcome === "refreshed") return apiRequest<T>(path, options, true);

    // The API has stopped honouring this local session (e.g. a consultant's
    // own-organisation portal whose organisation was reassigned). Drop it so the
    // next request falls back to the session underneath, or to the Entra identity,
    // instead of failing forever. The API has already refused it, so there is nothing
    // to revoke. An `unavailable` refresh proves nothing, so the session stays and the
    // caller sees the 401 as an ordinary error.
    if (outcome === "rejected") popLocalSession(false);
  }

  let payload: unknown = null;
  if (res.status !== 204) {
    try {
      payload = await res.json();
    } catch {
      if (!res.ok) throw new ApiError(res.status, null);
      throw new Error(`API returned non-JSON response for ${options.method ?? "GET"} ${path} (status ${res.status})`);
    }
  }
  if (!res.ok) throw new ApiError(res.status, payload);
  return payload as T;
}

// Fetches a binary response (CSV/PDF download) with full auth handling.
export async function apiBlob(path: string, query?: Record<string, string | undefined>): Promise<Blob> {
  const authHeader = await getAuthHeader();
  const url = new URL(path, API_BASE_URL);
  if (_viewingOrgId) url.searchParams.set("organizationId", _viewingOrgId);
  if (query) {
    for (const [k, v] of Object.entries(query)) if (v !== undefined) url.searchParams.set(k, v);
  }
  const res = await fetch(url.toString(), {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  return res.blob();
}

// Opens a raw fetch stream for SSE endpoints. The caller is responsible for
// reading the response body and parsing "data: <json>" lines.
export async function apiStream(path: string, signal?: AbortSignal): Promise<Response> {
  const authHeader = await getAuthHeader();
  const url = new URL(path, API_BASE_URL);
  if (_viewingOrgId) url.searchParams.set("organizationId", _viewingOrgId);
  return fetch(url.toString(), {
    headers: {
      Accept: "text/event-stream",
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
    signal,
  });
}

export const api = {
  get: <T>(path: string, query?: Record<string, string | undefined>) => apiRequest<T>(path, { method: "GET", query }),
  post: <T>(path: string, body?: unknown, query?: Record<string, string | undefined>) => apiRequest<T>(path, { method: "POST", body, query }),
  postAbortable: <T>(path: string, body?: unknown, signal?: AbortSignal) => apiRequest<T>(path, { method: "POST", body, signal }),
  patch: <T>(path: string, body?: unknown, query?: Record<string, string | undefined>) => apiRequest<T>(path, { method: "PATCH", body, query }),
  put: <T>(path: string, body?: unknown, query?: Record<string, string | undefined>) => apiRequest<T>(path, { method: "PUT", body, query }),
  del: <T>(path: string, query?: Record<string, string | undefined>) => apiRequest<T>(path, { method: "DELETE", query }),
};
